import {
  OrderBillEstimateItemResponseDto,
  OrderBillEstimateResponseDto,
  TaxItem,
} from '@atlassian/commerce-ccp-gateway-client';
import { PartnerOrderEstimate } from '@css-xp/ppc-types';
import { AdministrationFeePricing, TaxBreakdown } from '@css-xp/ppc-types/src/order-estimate-types';
import { PARTNER_DISCOUNT_TYPE } from '@css-xp/ppc-constants';

export const mapEstimate = (estimate: OrderBillEstimateResponseDto) => {
  const { orderIntentId, items: estimateItems } = estimate;

  const items = unwrapOrDefaultArray(estimateItems).map((item) => mapEstimateLineItem(item));
  const partnerPricing = tallyOverallPricing(items);
  const taxBreakdown = tallyOverallTaxBreakdown(items.flatMap((item) => item.taxBreakdown));

  const target: PartnerOrderEstimate.Response = {
    orderIntentId: orderIntentId as string,
    items,
    partnerPricing,
    taxBreakdown,
  };

  return target;
};

// this method is used to map and calculate overall estimate for the products and admin fees separately
export const mapEstimateV2 = (estimate: OrderBillEstimateResponseDto) => {
  const { orderIntentId, items: estimateItems } = estimate;

  const items = unwrapOrDefaultArray(estimateItems).map((item) => mapEstimateLineItem(item));
  const partnerPricing = tallyOverallPricingV2(items);
  const taxBreakdown = tallyOverallTaxBreakdown(items.flatMap((item) => item.taxBreakdown));
  const administrationFeePricing = tallyOverallAdminPricing(partnerPricing, items);

  const target: PartnerOrderEstimate.Response = {
    orderIntentId: orderIntentId as string,
    items,
    partnerPricing,
    taxBreakdown,
    administrationFeePricing,
  };

  return target;
};

// https://hello.atlassian.net/wiki/spaces/~574663882/pages/2578472450/PPC+Order+Summary+Page+calculations
const mapEstimateLineItem = (item: OrderBillEstimateItemResponseDto): PartnerOrderEstimate.Item => {
  const subItems = unwrapOrDefaultArray(item.items).map((subItem) => subItem as PartnerOrderEstimate.SubItem);

  const subItemsWithoutAdminFees = subItems.filter((subItem) => !subItem.metadata);
  const adjustments = subItemsWithoutAdminFees.flatMap((subItem) => unwrapOrDefaultArray(subItem.adjustments));
  const totalDiscount = adjustments.reduce((prev, next) => prev + (next.amount ?? 0), 0);
  const totalPartnerDiscount = adjustments
    .filter((adjustment) => adjustment.type === PARTNER_DISCOUNT_TYPE)
    .reduce((prev, next) => prev + (next.amount ?? 0), 0);
  const totalOtherDiscount = adjustments
    .filter((adjustment) => adjustment.type !== PARTNER_DISCOUNT_TYPE)
    .reduce((prev, next) => prev + (next.amount ?? 0), 0);
  const taxItems = subItems.flatMap((subItem) => unwrapOrDefaultArray(subItem.taxItems));
  const listPrice = subItemsWithoutAdminFees.reduce((acc, subItem) => acc + (subItem.subtotal ?? 0), 0);
  const totalPriceAfterDiscountAndBeforeTaxes = subItemsWithoutAdminFees.reduce(
    (acc, subItem) => acc + (subItem.total ?? 0),
    0
  );

  const totalExcludingDiscountAndTax = unwrapOrDefaultNumber(item.total) + totalDiscount;
  const totalExcludingTax = unwrapOrDefaultNumber(item.total);
  const totalTax = unwrapOrDefaultNumber(item.totalTax);
  const taxItemsWithoutAdminFees = subItemsWithoutAdminFees.flatMap((subItem) =>
    unwrapOrDefaultArray(subItem.taxItems)
  );
  const totalTaxWithoutAdminFeeTax = subItemsWithoutAdminFees.reduce((acc, subItem) => acc + (subItem.tax ?? 0), 0);
  const taxBreakdownWithoutAdminFees = mapTaxBreakdown(taxItemsWithoutAdminFees);
  const grandTotal = totalPriceAfterDiscountAndBeforeTaxes + totalTaxWithoutAdminFeeTax;

  const subItemsOnlyAdminFees = subItems.filter((subItem) => !!subItem.metadata);
  const isAdminFeeApplicable = subItemsOnlyAdminFees.length > 0;
  const dstTaxItems = subItemsOnlyAdminFees.flatMap((subItem) => unwrapOrDefaultArray(subItem.taxItems));
  const adminFeeTax = subItemsOnlyAdminFees.reduce((acc, subItem) => acc + (subItem.tax ?? 0), 0);
  const taxBreakdownForAdminFees = mapTaxBreakdown(dstTaxItems);
  const totalAdministrationFee = subItemsOnlyAdminFees.reduce((acc, subItem) => acc + (subItem.subtotal ?? 0), 0);
  const grandTotalAdminFeeWithTax = totalAdministrationFee + adminFeeTax;

  const billPeriod = mapBillPeriod(subItems);
  const taxBreakdown = mapTaxBreakdown(taxItems);
  // const adminFee = mapAdminFee(dstTaxItems);
  const percentageOfAdminFee = 2;
  const adminFee = isAdminFeeApplicable
    ? {
        amount: totalAdministrationFee,
        percent: percentageOfAdminFee,
      }
    : undefined;

  return {
    ...item,
    billPeriod,
    isAdminFeeApplicable,
    adminFee,
    percentageOfAdminFee,
    taxBreakdown,
    taxBreakdownWithoutAdminFees,
    taxBreakdownForAdminFees,
    partnerPricing: {
      totalDiscount,
      totalPartnerDiscount,
      totalOtherDiscount,
      totalExcludingDiscountAndTax,
      totalExcludingTax,
      totalTax,
      grandTotal,
      adjustments,
      taxItems,
      dstTaxItems,
      listPrice,
      totalPriceAfterDiscountAndBeforeTaxes,
      taxItemsWithoutAdminFees,
      adminFeeTax,
      grandTotalAdminFeeWithTax,
      totalTaxWithoutAdminFeeTax,
    },
  };
};

export const mapAdminFee = (taxItems: TaxItem[]): PartnerOrderEstimate.AdminFee | undefined => {
  // Only DST items are called here

  if (taxItems.length !== 0) {
    let totalDstAmount = 0;
    taxItems.forEach((taxItem) => {
      const { tax } = taxItem;
      if (tax) totalDstAmount += tax;
    });

    return {
      amount: totalDstAmount,
      // DST % = 2%
      percent: 2,
    };
  }
};

const mapTaxBreakdown = (taxItems: TaxItem[]): PartnerOrderEstimate.TaxBreakdown[] => {
  // No DST items are called here
  const taxBreakdown = new Map<string, PartnerOrderEstimate.TaxBreakdown>();

  // Grouping all tax items by tax labels, adding the tax amount from multiple items
  taxItems.forEach((taxItem) => {
    const { taxAmountLabel, taxPercent, tax } = taxItem;
    if (tax && taxAmountLabel && taxPercent) {
      let existing: TaxBreakdown | undefined;
      if ((existing = taxBreakdown.get(taxAmountLabel))) {
        // if exists, increment the amount
        existing.amount = roundTo2Decimals(existing.amount + tax);
      } else {
        // create new entry
        const breakDownItem = {
          amount: tax,
          label: taxAmountLabel,
          percent: taxPercent,
        };
        taxBreakdown.set(taxAmountLabel, breakDownItem);
      }
    }
  });

  return Array.from(taxBreakdown.values());
};

// grouping and adding all item level breakdowns by label
const tallyOverallTaxBreakdown = (
  itemLevelBreakdowns: PartnerOrderEstimate.TaxBreakdown[]
): PartnerOrderEstimate.TaxBreakdown[] => {
  const taxBreakdown = new Map<string, PartnerOrderEstimate.TaxBreakdown>();

  // Grouping all tax items by tax labels, adding the tax amount from multiple items
  itemLevelBreakdowns.forEach((taxItem) => {
    const { amount, percent, label } = taxItem;
    let existing: TaxBreakdown | undefined;
    if ((existing = taxBreakdown.get(label))) {
      // if exists, increment the amount
      existing.amount = roundTo2Decimals(existing.amount + amount);
    } else {
      // create new entry
      const breakDownItem = {
        amount,
        label,
        percent,
      };
      taxBreakdown.set(label, breakDownItem);
    }
  });

  return Array.from(taxBreakdown.values());
};

const mapBillPeriod = (estimateSubItems: PartnerOrderEstimate.SubItem[]): PartnerOrderEstimate.BillPeriod => {
  if (estimateSubItems.length === 0) {
    return {
      start: 0,
      end: 0,
    };
  }

  const subItemWithHighestTotal = estimateSubItems.reduce((maxSubItem, currentSubItem) => {
    const currentTotal = currentSubItem.total ?? 0;
    const maxTotal = maxSubItem.total ?? 0;
    return currentTotal > maxTotal ? currentSubItem : maxSubItem;
  }, estimateSubItems[0]);

  return {
    start: subItemWithHighestTotal.billPeriod?.start ?? 0,
    end: subItemWithHighestTotal.billPeriod?.end ?? 0,
  };
};

const tallyOverallPricing = (items: Array<PartnerOrderEstimate.Item>): PartnerOrderEstimate.OverallPricing => {
  const overallPricing = items.reduce<PartnerOrderEstimate.OverallPricing>(
    (pricing, currentItem) => {
      const {
        grandTotal,
        totalDiscount,
        totalPartnerDiscount,
        totalOtherDiscount,
        totalExcludingDiscountAndTax,
        totalExcludingTax,
        totalTax,
        listPrice,
      } = currentItem.partnerPricing;

      pricing.grandTotal += grandTotal;
      pricing.totalDiscount += totalDiscount;
      pricing.totalPartnerDiscount += totalPartnerDiscount;
      pricing.totalOtherDiscount += totalOtherDiscount;
      pricing.totalExcludingDiscountAndTax += totalExcludingDiscountAndTax;
      pricing.totalExcludingTax += totalExcludingTax;
      pricing.totalTax += totalTax;
      pricing.totalAdminFee += currentItem.adminFee?.amount ?? 0;
      pricing.totalListPrice += listPrice;
      return pricing;
    },
    {
      totalDiscount: 0,
      totalPartnerDiscount: 0,
      totalOtherDiscount: 0,
      totalExcludingDiscountAndTax: 0,
      totalExcludingTax: 0,
      totalTax: 0,
      grandTotal: 0,
      totalAdminFee: 0,
      totalListPrice: 0,
      totalAdminFeeTax: 0,
      totalAdminFeeWithTax: 0,
    }
  );
  return overallPricing;
};

/**
 * calculate overall pricing for the estimate
 * grand total is calculated by summing up grand total of each item, admin fee and admin fee tax
 * total admin fee is calculated by summing up admin fee of each item
 */
const tallyOverallPricingV2 = (items: Array<PartnerOrderEstimate.Item>): PartnerOrderEstimate.OverallPricing => {
  const overallPricing = items.reduce<PartnerOrderEstimate.OverallPricing>(
    (pricing, currentItem) => {
      const {
        grandTotal,
        totalDiscount,
        totalPartnerDiscount,
        totalOtherDiscount,
        totalExcludingDiscountAndTax,
        totalExcludingTax,
        totalTax,
        listPrice,
        adminFeeTax,
        grandTotalAdminFeeWithTax,
      } = currentItem.partnerPricing;
      const adminFee = currentItem.adminFee?.amount ?? 0;
      const grandTotalValue = grandTotal + adminFee + adminFeeTax;
      pricing.grandTotal += grandTotalValue;
      pricing.totalDiscount += totalDiscount;
      pricing.totalPartnerDiscount += totalPartnerDiscount;
      pricing.totalOtherDiscount += totalOtherDiscount;
      pricing.totalExcludingDiscountAndTax += totalExcludingDiscountAndTax;
      pricing.totalExcludingTax += totalExcludingTax;
      pricing.totalTax += totalTax;
      pricing.totalAdminFee += adminFee;
      pricing.totalListPrice += listPrice;
      pricing.totalAdminFeeTax += adminFeeTax;
      pricing.totalAdminFeeWithTax += grandTotalAdminFeeWithTax;
      return pricing;
    },
    {
      totalDiscount: 0,
      totalPartnerDiscount: 0,
      totalOtherDiscount: 0,
      totalExcludingDiscountAndTax: 0,
      totalExcludingTax: 0,
      totalTax: 0,
      grandTotal: 0,
      totalAdminFee: 0,
      totalListPrice: 0,
      totalAdminFeeTax: 0,
      totalAdminFeeWithTax: 0,
    }
  );
  return overallPricing;
};

// if admin fees are available, calculate total admin fee, total admin fee tax, total admin fee with tax and tax breakdown
const tallyOverallAdminPricing = (
  pricing: PartnerOrderEstimate.OverallPricing,
  items: Array<PartnerOrderEstimate.Item>
): AdministrationFeePricing | undefined => {
  const isAdminFeeApplicable = items.some((item) => item.isAdminFeeApplicable);
  if (!isAdminFeeApplicable) {
    return undefined; // we should send undefined if no admin fees are available
  }
  return {
    administrationFeeListPrice: pricing.totalAdminFee,
    totalAdminFeeExcludingDiscountAndTax: pricing.totalAdminFee,
    totalAdminFeeTax: pricing.totalAdminFeeTax,
    totalAdminFeeWithTax: pricing.totalAdminFeeWithTax,
    taxBreakdown: items.flatMap((item) => item.taxBreakdownForAdminFees),
    currency: items.find((item) => item.currency)?.currency ?? 'USD',
  };
};

const unwrapOrDefaultNumber = (num?: number) => {
  if (num) {
    return num as number;
  } else {
    return 0;
  }
};

const unwrapOrDefaultArray = <T>(arr?: Array<T>) => {
  if (arr) {
    return arr;
  } else {
    return new Array<T>();
  }
};

const roundTo2Decimals = (num: number) => {
  return Math.round((num + Number.EPSILON) * 100) / 100;
};
